import { createContext, useContext, useEffect, useMemo, useState } from "react";

const LIGHT_THEME = "light";
const DARK_THEME = "dark";
const THEME_STORAGE_KEY = "click4driver-admin-theme-mode";
const LEGACY_THEME_STORAGE_KEY = "toggled";
const LIGHT_BACKGROUND_TOKEN = "theme-background-F2F7FB";
const DARK_BACKGROUND_TOKEN = "theme-background-1F2027";

const ThemeContext = createContext(null);

const getInitialThemeMode = () => {
    if (typeof window === "undefined") {
        return LIGHT_THEME;
    }

    const savedThemeMode = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (savedThemeMode === LIGHT_THEME || savedThemeMode === DARK_THEME) {
        return savedThemeMode;
    }

    const legacyThemeMode = window.localStorage.getItem(LEGACY_THEME_STORAGE_KEY);
    if (legacyThemeMode === "dark-theme") {
        return DARK_THEME;
    }

    if (legacyThemeMode === "light-theme") {
        return LIGHT_THEME;
    }

    if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
        return DARK_THEME;
    }

    return LIGHT_THEME;
};

export const ThemeProvider = ({ children }) => {
    const [themeMode, setThemeMode] = useState(getInitialThemeMode);

    useEffect(() => {
        if (typeof document === "undefined") {
            return;
        }

        const isDarkMode = themeMode === DARK_THEME;
        const bodyThemeClass = isDarkMode ? "dark-theme" : "light-theme";

        document.body.classList.remove("dark-theme", "light-theme");
        document.body.classList.add(bodyThemeClass);
        document.body.dataset.theme = themeMode;
        document.body.dataset.themeBackground = isDarkMode
            ? DARK_BACKGROUND_TOKEN
            : LIGHT_BACKGROUND_TOKEN;
        document.documentElement.dataset.theme = themeMode;
        document.documentElement.style.colorScheme = themeMode;

        window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
        window.localStorage.setItem(LEGACY_THEME_STORAGE_KEY, bodyThemeClass);
    }, [themeMode]);

    const toggleTheme = () => {
        setThemeMode((currentThemeMode) =>
            currentThemeMode === DARK_THEME ? LIGHT_THEME : DARK_THEME
        );
    };

    const value = useMemo(
        () => ({
            themeMode,
            isDarkMode: themeMode === DARK_THEME,
            setThemeMode,
            toggleTheme,
        }),
        [themeMode]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useThemeMode = () => {
    const context = useContext(ThemeContext);

    if (!context) {
        throw new Error("useThemeMode must be used within a ThemeProvider");
    }

    return context;
};
